import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useIsDisabledByPermission } from '../contexts/PermissionAccessContext';
import { useFieldShellDisplay } from '../contexts/DisplayPreferencesContext';

/**
 * Same options shape as MultiSelect — so callers can share option lists
 * between the two components.
 */
export interface SingleSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  isDivider?: boolean;
  isBold?: boolean;
  isHeader?: boolean;
}

interface SingleSelectProps {
  options: SingleSelectOption[];
  value: string;                       // empty string = no selection
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  showClearOption?: boolean;
  clearOptionLabel?: string;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  size?: 'sm' | 'md';
  dropdownDirection?: 'up' | 'down';
  usePortal?: boolean;
  /** aria-label for the trigger button (for screen readers / getByLabelText). */
  ariaLabel?: string;
  /** Surface the currently-selected option at the top of the list on open,
   *  instead of leaving it in the list's natural order. */
  pinSelectedTop?: boolean;
}

/**
 * Searchable, scrollable single-select dropdown.
 *
 * Visual + behavioral twin of {@link MultiSelect}, but constrained to
 * exactly one selection at a time. Clicking an option replaces the
 * current selection and closes the popup (unlike MultiSelect which
 * toggles into an array).
 *
 * Why a parallel component instead of a `singleSelect` prop on
 * MultiSelect: the value contract differs (`string` vs `string[]`),
 * the display logic is simpler (no count-mode, no comma-joined
 * labels), and close-on-select changes the interaction model enough
 * that branching on a flag inside MultiSelect made both paths messier
 * than just having two components with shared option types.
 */
const SingleSelect: React.FC<SingleSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  disabled: disabledProp = false,
  loading = false,
  showClearOption = false,
  clearOptionLabel = 'None',
  enableSearch = false,
  searchPlaceholder = 'Search...',
  size = 'md',
  dropdownDirection = 'down',
  usePortal = false,
  ariaLabel,
  pinSelectedTop = false,
}) => {
  const disabledByPermission = useIsDisabledByPermission();
  const disabled = disabledProp || disabledByPermission;
  // Field display prefs: plain => label as text, no box/chevron; the class
  // marks this trigger as the box for the CSS-only options (tint, color, fade).
  const { plain: plainLocked, className: shellClass } = useFieldShellDisplay(disabled);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click-outside.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideContainer && !insideDropdown) setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Portal positioning.
  useEffect(() => {
    if (isOpen && usePortal && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: dropdownDirection === 'up' ? rect.top - 8 : rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen, usePortal, dropdownDirection]);

  // Focus search + reset query on open/close.
  useEffect(() => {
    if (isOpen && enableSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isOpen) setSearchQuery('');
  }, [isOpen, enableSearch]);

  const selectOption = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const clearSelection = () => {
    onChange('');
    setIsOpen(false);
  };

  const matchesSearch = (option: SingleSelectOption): boolean => {
    if (!enableSearch || !searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      String(option.label ?? '').toLowerCase().includes(q) ||
      String(option.value ?? '').toLowerCase().includes(q)
    );
  };

  const selectedOption = options.find((o) => o.value === value);
  const displayText = selectedOption?.label || placeholder;

  // With pinSelectedTop, surface the current selection at the top of the list
  // (e.g. a defaulted last-traced carrier) instead of its natural alpha order.
  const visibleOptions = (() => {
    const vis = options.filter(matchesSearch);
    if (!pinSelectedTop || !value) return vis;
    const sel = vis.filter((o) => o.value === value && !o.isDivider && !o.isHeader);
    return sel.length ? [...sel, ...vis.filter((o) => o.value !== value)] : vis;
  })();

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={`${usePortal ? 'fixed' : 'absolute'} z-[9999] min-w-full w-max bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-900 dark:border-gray-700 ${
        !usePortal && dropdownDirection === 'up' ? 'bottom-full mb-2' : !usePortal ? 'mt-2' : ''
      }`}
      style={
        usePortal
          ? {
              top: dropdownDirection === 'up' ? 'auto' : dropdownPosition.top,
              bottom:
                dropdownDirection === 'up'
                  ? `calc(100vh - ${dropdownPosition.top}px + 8px)`
                  : 'auto',
              left: dropdownPosition.left,
              minWidth: dropdownPosition.width,
            }
          : undefined
      }
    >
      {/* Search input */}
      {enableSearch && (
        <div className={`${size === 'sm' ? 'p-1' : 'p-2'} border-b border-gray-200 dark:border-gray-700`}>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className={`w-full ${size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'} border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder-gray-400`}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Options list */}
      <div className="max-h-60 p-1 space-y-0.5 overflow-y-auto">
        {/* Clear option */}
        {showClearOption && (
          <div
            onClick={clearSelection}
            className={`${size === 'sm' ? 'py-1 px-2 text-xs' : 'py-2 px-4 text-sm'} w-full cursor-pointer hover:bg-blue-50 rounded-lg focus:outline-none focus:bg-blue-50 dark:hover:bg-blue-900/30 dark:focus:bg-blue-900/30 flex justify-between items-center text-blue-600 dark:text-blue-400 font-medium border-b border-gray-200 dark:border-gray-700 mb-1 whitespace-nowrap`}
          >
            <span>{clearOptionLabel}</span>
            {value === '' && (
              <CheckIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-500" />
            )}
          </div>
        )}
        {loading ? (
          <div className="py-4 px-4 text-sm text-center text-muted flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </div>
        ) : visibleOptions.length === 0 ? (
          <div className="py-4 px-4 text-sm text-center text-muted">
            No options found
          </div>
        ) : (
          visibleOptions.map((option, index) => {
            if (option.isDivider) {
              return (
                <div
                  key={`divider-${index}`}
                  className="border-b border-gray-200 dark:border-gray-700 my-1"
                />
              );
            }
            if (option.isHeader) {
              return (
                <div
                  key={`header-${index}`}
                  className={`${size === 'sm' ? 'py-1 px-2 text-xs' : 'py-1.5 px-4 text-xs'} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide`}
                >
                  {option.label}
                </div>
              );
            }
            const isSelected = option.value === value;
            return (
              <div
                key={option.value}
                onClick={() => selectOption(option.value)}
                className={`${size === 'sm' ? 'py-1 px-2 text-xs' : 'py-2 px-4 text-sm'} w-full cursor-pointer hover:bg-gray-100 rounded-lg focus:outline-none focus:bg-gray-100 dark:hover:bg-gray-800 dark:focus:bg-gray-800 flex justify-between items-center whitespace-nowrap ${
                  isSelected ? 'text-heading' : 'text-gray-800 dark:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                  <span className={option.isBold ? 'font-semibold' : ''}>{option.label}</span>
                </div>
                {isSelected && (
                  <CheckIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-500" />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-label={ariaLabel}
        className={`relative ${size === 'sm' ? 'py-1 ps-2 pe-6 text-xs' : 'py-1.5 ps-3 pe-9 text-sm'} flex gap-x-2 text-nowrap w-full cursor-pointer bg-white border border-gray-400 rounded-lg text-start focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-500 dark:focus:ring-gray-600 ${
          disabled ? 'opacity-50 pointer-events-none' : ''
        } ${!value ? 'text-gray-500' : 'text-heading'} ${shellClass}`}
        aria-expanded={isOpen}
      >
        <span className="truncate">{displayText}</span>
        {!plainLocked && (
          <div className={`absolute top-1/2 ${size === 'sm' ? 'end-1' : 'end-3'} -translate-y-1/2`}>
            <ChevronUpDownIcon className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} text-gray-500 dark:text-gray-500`} />
          </div>
        )}
      </button>

      {/* Inside a native <dialog> (Drawer uses showModal), document.body sits below
          the top layer and is inert — portal into the dialog itself, like MultiSelect. */}
      {isOpen && (usePortal
        ? createPortal(dropdownContent, containerRef.current?.closest('dialog') || document.body)
        : dropdownContent)}
    </div>
  );
};

export default SingleSelect;
